<template>
  <div class="sales-details">
    <!-- Quick periods -->
    <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />

    <!-- Filters + actions -->
    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 5">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Customer</label>
          <FormDropdown v-model="filters.customer" :options="customerOptions" placeholder="All customers" clearable />
        </div>
        <div class="rp-filter">
          <label>Warehouse</label>
          <FormDropdown v-model="filters.warehouse" :options="warehouseOptions" placeholder="All warehouses" clearable />
        </div>
        <div class="rp-filter">
          <label>Category</label>
          <FormDropdown v-model="filters.category" :options="categoryOptions" placeholder="All categories" clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Item / Invoice</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input
              v-model="filters.search"
              type="text"
              class="form-control"
              placeholder="Search item, code, SKU or invoice no."
            />
          </div>
        </div>
      </div>

      <ReportActions
        :loading="loading"
        :exporting="exporting"
        :can-reset="hasActiveFilters"
        :can-print="!!rows.length"
        @reset="resetFilters"
        @export="exportFile"
        @print="printReport"
      />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <!-- Summary cards -->
    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-shopping-bag"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Net Sales</div>
          <div class="rp-card-value">{{ formatMoney(summary.net) }}</div>
          <div class="rp-card-sub">{{ formatQty(summary.qty) }} units · {{ plural(summary.line_count, 'line') }}</div>
        </div>
      </div>
      <div class="rp-card tone-blue">
        <span class="rp-card-icon"><i class="ti ti-discount-2"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Discount</div>
          <div class="rp-card-value">{{ formatMoney(summary.discount) }}</div>
          <div class="rp-card-sub">{{ summary.discount_rate }}% of gross · {{ plural(summary.discounted_lines, 'line') }}</div>
        </div>
      </div>
      <div class="rp-card tone-green">
        <span class="rp-card-icon"><i class="ti ti-trending-up"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Gross Profit</div>
          <div class="rp-card-value" :class="netClass(summary.profit)">{{ formatMoney(summary.profit) }}</div>
          <div class="rp-card-sub">{{ summary.margin }}% margin on cost {{ formatMoney(summary.cost) }}</div>
        </div>
      </div>
      <div class="rp-card tone-navy">
        <span class="rp-card-icon"><i class="ti ti-file-invoice"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Invoices</div>
          <div class="rp-card-value">{{ formatInt(summary.invoice_count) }}</div>
          <div class="rp-card-sub">
            {{ plural(summary.product_count, 'product') }} · VAT {{ formatMoney(summary.vat) }}
          </div>
        </div>
      </div>
    </div>

    <!-- Insights -->
    <div class="rp-insights trend-first" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title d-flex justify-content-between align-items-center">
          <span>Sales &amp; profit {{ summary.trend_unit === 'month' ? 'by month' : 'by day' }}</span>
          <span class="rp-legend">
            <span><i class="rp-dot" :style="{ background: SALES_COLOR }"></i>Net sales</span>
            <span><i class="rp-dot" :style="{ background: PROFIT_COLOR }"></i>Profit</span>
          </span>
        </div>
        <div v-show="summary.trend.length" class="rp-chart"><canvas ref="trendChart"></canvas></div>
        <p v-if="!summary.trend.length" class="text-muted small mb-0">No sales in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Sales by category</div>
        <div v-if="categoryBars.length" class="rp-bars">
          <div v-for="category in categoryBars" :key="category.name">
            <div class="d-flex justify-content-between small mb-1">
              <span class="text-truncate me-2">{{ category.name }}</span>
              <span class="fw-semibold text-nowrap">
                {{ formatMoney(category.net) }} <span class="text-muted fw-normal">· {{ formatQty(category.qty) }}</span>
              </span>
            </div>
            <div class="rp-bar-track"><div class="rp-bar-fill" :style="{ width: `${category.percent}%` }"></div></div>
          </div>
        </div>
        <p v-else class="text-muted small mb-0">No sales in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Top products</div>
        <ul v-if="summary.top_items.length" class="rp-ranked">
          <li v-for="(item, index) in summary.top_items" :key="item.name">
            <span class="rp-rank">{{ index + 1 }}</span>
            <div class="flex-grow-1 min-w-0">
              <div class="text-truncate" :title="item.name">{{ item.name }}</div>
              <small class="text-muted">{{ formatQty(item.qty) }} sold · profit {{ formatMoney(item.profit) }}</small>
            </div>
            <span class="fw-semibold text-nowrap">{{ formatMoney(item.net) }}</span>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">No sales in this period.</p>
      </div>
    </div>

    <!-- Lines table -->
    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="fw-semibold text-body">
          <i class="ti ti-list-details me-1"></i>Invoice lines
          <span class="rp-pill-count ms-1">{{ formatInt(rows.length) }}</span>
          <span v-if="sortKey && sortKey !== 'invoice_no'" class="rp-hint ms-2">
            Sorted by {{ sortLabel }} — <a href="#" @click.prevent="clearSort">group by invoice</a>
          </span>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th v-for="column in columns" :key="column.key" :class="column.class" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}
                  <i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="{ row, span, group } in pagedLines" :key="row.key" :class="{ 'sd-group-start': span }">
              <td v-if="span" :rowspan="span" class="sd-invoice">
                <div class="fw-semibold text-nowrap">{{ row.invoice_no }}</div>
                <span class="rp-sub">{{ formatDate(row.date) }}</span>
                <div class="sd-customer text-truncate" :title="row.customer">
                  <i class="ti ti-user me-1"></i>{{ row.customer }}
                </div>
                <span v-if="group.lines > 1" class="rp-sub">{{ plural(group.lines, 'line') }} · {{ formatMoney(group.net) }}</span>
              </td>
              <td>
                <div class="fw-medium">
                  {{ row.item_name }}
                  <span v-if="Number(row.returned_qty)" class="rp-badge info ms-1" :title="`${formatQty(row.returned_qty)} returned since`">
                    <i class="ti ti-arrow-back-up"></i>{{ formatQty(row.returned_qty) }} returned
                  </span>
                </div>
                <span class="rp-sub">
                  {{ row.item_code }}<template v-if="row.batch_no"> · Batch {{ row.batch_no }}</template>
                </span>
                <span v-for="part in variantParts(row)" :key="part" class="rp-variant mt-1">{{ part }}</span>
              </td>
              <td class="text-end fw-semibold">
                {{ formatQty(row.qty) }}<small v-if="row.unit" class="text-muted ms-1">{{ row.unit }}</small>
              </td>
              <td class="text-end">{{ formatMoney(row.unit_price) }}</td>
              <td class="text-end">{{ formatMoney(row.gross) }}</td>
              <td class="text-end">
                <template v-if="Number(row.discount)">
                  −{{ formatMoney(row.discount) }}
                  <span class="rp-sub">{{ row.discount_pct }}%</span>
                </template>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-end" :class="{ 'text-muted': !Number(row.vat) }">
                {{ Number(row.vat) ? formatMoney(row.vat) : '—' }}
              </td>
              <td class="text-end fw-semibold">{{ formatMoney(row.net) }}</td>
              <td class="text-end">
                <span :class="netClass(row.profit)">{{ formatMoney(row.profit) }}</span>
                <span class="rp-sub">{{ row.margin }}%</span>
              </td>
            </tr>

            <tr v-if="!loading && !pagedLines.length">
              <td :colspan="columns.length">
                <div class="rp-empty">
                  <i class="ti ti-list-details"></i>
                  <div class="fw-semibold">No sales found</div>
                  <small class="text-muted">Try another period, customer or search.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length">
            <tr>
              <th colspan="2" class="text-end">
                Total ({{ plural(summary.invoice_count, 'invoice') }} · {{ plural(rows.length, 'line') }})
              </th>
              <th class="text-end">{{ formatQty(summary.qty) }}</th>
              <th></th>
              <th class="text-end">{{ formatMoney(summary.gross) }}</th>
              <th class="text-end">{{ Number(summary.discount) ? `−${formatMoney(summary.discount)}` : '—' }}</th>
              <th class="text-end">{{ formatMoney(summary.vat) }}</th>
              <th class="text-end">{{ formatMoney(summary.net) }}</th>
              <th class="text-end" :class="netClass(summary.profit)">{{ formatMoney(summary.profit) }}</th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination
        v-if="visibleRows.length"
        :page="page"
        :page-count="pageCount"
        :pages="pageNumbers"
        :start="pageStart"
        :end="pageEnd"
        :total="visibleRows.length"
        @go="goToPage"
      />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Net = gross − discount + VAT. Profit = gross − discount − batch cost of the units sold.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport, { escapeHtml } from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod } from '../inventory/periodReportMixin';
import salesReport, { PROFIT_COLOR, SALES_COLOR } from './salesReportMixin';

const EMPTY_SUMMARY = {
  gross: 0, discount: 0, vat: 0, net: 0, cost: 0, qty: 0, profit: 0, returned_qty: 0, line_count: 0,
  invoice_count: 0, product_count: 0, discounted_lines: 0, discount_rate: 0, margin: 0, by_category: [],
  top_items: [], trend_unit: 'day', trend: [],
};

export default {
  name: 'SalesDetailsReport',
  mixins: [inventoryReport, periodReport, salesReport],

  reportUrl: apiUrls.sales_details_report.get,
  fileName: 'sales_details',
  columns: [
    { key: 'invoice_no', label: 'Invoice' },
    { key: 'item_name', label: 'Product' },
    { key: 'qty', label: 'Qty', class: 'text-end', numeric: true },
    { key: 'unit_price', label: 'Unit Price', class: 'text-end', numeric: true },
    { key: 'gross', label: 'Gross', class: 'text-end', numeric: true },
    { key: 'discount', label: 'Discount', class: 'text-end', numeric: true },
    { key: 'vat', label: 'VAT', class: 'text-end', numeric: true },
    { key: 'net', label: 'Net', class: 'text-end', numeric: true },
    { key: 'profit', label: 'Profit', class: 'text-end', numeric: true },
  ],

  data() {
    return {
      filters: {
        ...defaultPeriod(), customer: null, warehouse: null, category: null, sub_category: null, search: '',
      },
      summary: { ...EMPTY_SUMMARY },
      sortKey: null,
      sortDir: 'desc',
      SALES_COLOR,
      PROFIT_COLOR,
    };
  },

  computed: {
    hasActiveFilters() {
      return this.periodChanged || this.itemFiltersActive || !!this.filters.customer;
    },

    categoryBars() {
      return this.withPercent(this.summary.by_category, 'net');
    },

    sortLabel() {
      return (this.columns.find((c) => c.key === this.sortKey)?.label || '').toLowerCase();
    },

    visibleRows() {
      // Without a chosen column the server's order (newest invoice first) keeps each invoice's lines together.
      return this.sortKey ? this.sortRows(this.rows) : this.rows;
    },

    /** The page's rows, each with how many following rows (itself included) belong to the same invoice. */
    pagedLines() {
      const rows = this.pagedRows;
      const groups = {};
      this.visibleRows.forEach((row) => {
        const group = groups[row.invoice_no] || (groups[row.invoice_no] = { lines: 0, net: 0 });
        group.lines += 1;
        group.net += Number(row.net) || 0;
      });
      return rows.map((row, index) => {
        if (index && rows[index - 1].invoice_no === row.invoice_no) return { row, span: 0 };
        let span = 1;
        while (rows[index + span]?.invoice_no === row.invoice_no) span += 1;
        return { row, span, group: groups[row.invoice_no] };
      });
    },
  },

  methods: {
    extraParams() {
      return { ...this.periodParams(), ...this.customerParams() };
    },

    resetExtraFilters() {
      this.sortKey = null;
      return { ...defaultPeriod(this.today), customer: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
      this.$nextTick(() => this.drawMoneyTrend(this.$refs.trendChart, [
        { label: 'Net sales', field: 'net', color: SALES_COLOR },
        { label: 'Profit', field: 'profit', color: PROFIT_COLOR, line: true },
      ]));
    },

    clearSort() {
      this.sortKey = null;
      this.page = 1;
    },

    variantParts(row) {
      return row.variant ? row.variant.split(' · ') : [];
    },

    printReport() {
      const s = this.summary;
      const body = this.visibleRows.map((r) => `
        <tr>
          <td>${this.formatDate(r.date)}</td>
          <td>${escapeHtml(r.invoice_no)}</td>
          <td>${escapeHtml(r.customer)}</td>
          <td>${escapeHtml(r.item_code)} · ${escapeHtml(r.item_name)}${r.variant ? `<br><small>${escapeHtml(r.variant)}</small>` : ''}</td>
          <td class="num">${this.formatQty(r.qty)}</td>
          <td class="num">${this.formatMoney(r.unit_price)}</td>
          <td class="num">${this.formatMoney(r.gross)}</td>
          <td class="num">${this.formatMoney(r.discount)}</td>
          <td class="num">${this.formatMoney(r.vat)}</td>
          <td class="num">${this.formatMoney(r.net)}</td>
          <td class="num">${this.formatMoney(r.profit)}</td>
        </tr>`).join('');
      const table = `<table><thead><tr><th>Date</th><th>Invoice</th><th>Customer</th><th>Product</th>
          <th class="num">Qty</th><th class="num">Unit Price</th><th class="num">Gross</th><th class="num">Discount</th>
          <th class="num">VAT</th><th class="num">Net</th><th class="num">Profit</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr><td colspan="4" class="num">Total</td><td class="num">${this.formatQty(s.qty)}</td><td></td>
          <td class="num">${this.formatMoney(s.gross)}</td><td class="num">${this.formatMoney(s.discount)}</td>
          <td class="num">${this.formatMoney(s.vat)}</td><td class="num">${this.formatMoney(s.net)}</td>
          <td class="num">${this.formatMoney(s.profit)}</td></tr></tfoot></table>`;
      this.openPrint(
        'Sales Details',
        [
          [this.periodText(), this.customerText(), ...this.itemFiltersText()].join(' · '),
          `Printed ${new Date().toLocaleString()}`,
        ],
        [
          ['Net Sales', `${this.formatMoney(s.net)} · ${this.formatQty(s.qty)} units`],
          ['Discount', `${this.formatMoney(s.discount)} (${s.discount_rate}%)`],
          ['Gross Profit', `${this.formatMoney(s.profit)} (${s.margin}%)`],
          ['Invoices', `${this.formatInt(s.invoice_count)} · ${this.plural(s.line_count, 'line')}`],
        ],
        table,
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>

<style scoped>
.sd-invoice {
  width: 190px;
  max-width: 220px;
  border-right: 1px solid #F0F2F5;
  background: #FCFCFD;
  vertical-align: top !important;
}

.sd-customer {
  margin-top: 0.35rem;
  color: #3A4652;
  font-size: 0.8rem;
}

.rp-table > tbody > tr.sd-group-start > td {
  border-top: 1px solid #E6EAED;
}
</style>
